/* eslint-disable react-hooks/exhaustive-deps */
import Hls from "hls.js";
import { motion } from "motion/react";
import { useDebouncedCallback } from "use-debounce";

import { JSX, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Icon from "@/components/common/Icon/Icon.tsx";

import { ROUTES } from "@/config/routes.ts";
import { useGetContentDetail } from "@/hooks/api/content.hooks.ts";
import { cn } from "@/utils/cn.ts";
import { isSafari } from "@/utils/constatns";

import "./styles.css";

interface MoviePreviewVideoProps {
  isBeingHidden: boolean;
  id: number;
  previewImg: string;
}

const MoviePreviewVideo = ({ isBeingHidden, id, previewImg }: MoviePreviewVideoProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const backgroundRef = useRef<HTMLImageElement | null>(null);
  // const requestIdRef = useRef<number | null>(null);
  const [isCardMuted, setIsCardMuted] = useState<boolean>(true);
  const { data: content } = useGetContentDetail(id);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvas = canvasRef?.current;

  const ctx = canvas?.getContext("2d", { willReadFrequently: true });
  const teaserLink = content?.trailers[0]?.trailer_link;

  const draw = () => {
    const video = videoRef.current;
    if (!video || !canvas || !ctx) return;

    if (video.readyState < 2 || video.paused || video.ended || !isVideoReady) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    ctx.canvas.style.transform = "scale(1.3)";

    debounce();
  };

  const debounce = useDebouncedCallback(() => requestAnimationFrame(draw), 50);

  useEffect(() => {
    if (!ctx) return;

    requestAnimationFrame(draw);

    return () => {
      ctx.canvas.style.transform = "scale(0)";
    };
  }, [isVideoReady, isBeingHidden]);

  useEffect(() => {
    let isMounted = true;
    let hls: Hls;

    if (videoRef.current && content && teaserLink) {
      const videoPlayer = videoRef.current;
      videoPlayer.volume = 1;
      videoPlayer.playbackRate = 1;

      hls = new Hls();

      hls.loadSource(teaserLink);
      hls.attachMedia(videoPlayer);

      const handleManifestParsed = async () => {
        if (isMounted) {
          hls.levels.forEach((level, levelIndex) => {
            if (level.name === "480") {
              hls.currentLevel = levelIndex;
            }
          });
          try {
            await videoPlayer.play();
          } catch (error) {
            console.error("Error playing video:", error);
          }
        }
      };

      hls.on(Hls.Events.MANIFEST_PARSED, handleManifestParsed);

      return () => {
        if (videoPlayer) {
          videoPlayer.pause();
          videoPlayer.src = "";
        }
        if (hls) {
          hls.off(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
          hls.destroy();
        }
        isMounted = false;
      };
    }
  }, [content]);

  useEffect(() => {
    if (videoRef.current && isVideoReady) {
      const videoPlayer = videoRef.current;
      videoPlayer.addEventListener("timeupdate", () => {
        if (videoPlayer.currentTime > 0.1 && !isVideoPlaying) {
          setIsVideoPlaying(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoReady]);

  useEffect(() => {
    if (isBeingHidden && videoRef.current) {
      const videoPlayer = videoRef.current;
      videoPlayer.pause();
      videoPlayer.currentTime = 0;
      videoPlayer.load();
      setIsVideoPlaying(false);
    }
  }, [isBeingHidden]);

  return (
    <>
      <motion.div
        className={"relative z-[10] aspect-video overflow-clip bg-black"}
        initial={{ borderRadius: "0.75rem" }}
        exit={{ borderRadius: "0.75rem" }}
        animate={{ borderRadius: "0rem" }}
      >
        <Link
          to={content ? ROUTES.DETAIL.MAIN.replace(":id", content!.id.toString()) : ""}
          className={cn("movie-preview-video flex w-full items-center justify-center")}
        >
          <motion.video
            ref={videoRef}
            autoPlay={isVideoReady}
            muted={isCardMuted}
            poster={previewImg}
            className={"h-full w-full object-cover"}
            onCanPlay={() => {
              setIsVideoReady(true);
            }}
          />
        </Link>
        <button
          type={"button"}
          onClick={() => setIsCardMuted((prev) => !prev)}
          title={isCardMuted ? t("sound-on") : t("sound-off")}
          className={cn("absolute bottom-4 right-4 rounded-full border border-on-primary p-2", {
            hidden: !isVideoPlaying,
          })}
        >
          <Icon name={isCardMuted ? "muted" : "unmuted"} className={"size-4 fill-on-primary stroke-on-primary"} />
        </button>
        {isVideoPlaying && content && (
          <img
            src={content.logo_image_rectangle?.small ?? content.logo_image?.small}
            alt={content.title}
            loading={"lazy"}
            className={"absolute bottom-4 left-4 h-5"}
          />
        )}
      </motion.div>
      <motion.img
        ref={backgroundRef}
        src={previewImg}
        initial={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        animate={{ opacity: !isSafari && isVideoReady ? 0 : 1 }}
        transition={{ delay: isVideoPlaying ? 400 : 0 }}
        loading={"lazy"}
        className={cn("movie-preview-video__background absolute inset-0 z-[-2] h-full w-full scale-100 brightness-75")}
      />
      {!isSafari && (
        <canvas
          ref={canvasRef}
          width={40}
          height={20}
          className="ambient-canvas z-[-1] aspect-[200/110] !blur-xl transition-all  max-xl:hidden"
        />
      )}
    </>
  );
};

export default MoviePreviewVideo;
